// @flow
import React, { Component, Fragment } from "react";
import * as d3 from "d3";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import HelpIcon from "@material-ui/icons/Help";

import type { ChartType } from "../../../global//types/BillingChartType";
import { ChartColors } from "../../../global/types/ChartColors";
import { LoadingProgress } from "imagus-components";
import { AppendHelpInfo } from "../../../admin/containers/billings/compileBillingNode";

type SlicePropsType = {
  item: { [key: string]: string },
  color: string,
  selectSlice: Function
};

type SliceStatesType = {
  isHover: boolean
};

class Slice extends Component<SlicePropsType, SliceStatesType> {
  constructor(props: SlicePropsType) {
    super(props);
    this.state = {
      isHover: false
    };
  }

  onMouseOver = (): * => {
    this.setState({ isHover: true });
    this.props.selectSlice(this.props.item.data.id);
  };

  onMouseOut = (): * => {
    this.setState({ isHover: false });
    this.props.selectSlice(null);
  };

  render(): React$Node {
    const { item, color } = this.props;
    let arc = d3
      .arc()
      .innerRadius(50)
      .outerRadius(this.state.isHover ? 170 : 160)
      .cornerRadius(7)
      .padAngle(0.02);

    return (
      <g onMouseOver={this.onMouseOver} onMouseOut={this.onMouseOut}>
        <path d={arc(item)} fill={color} />
      </g>
    );
  }
}

type BillingTablePropsType = {
  charts: Array<ChartType>,
  total: string
};

type BillingTableStatesType = {
  selected: string | null
};

class BillingTable extends Component<
  BillingTablePropsType,
  BillingTableStatesType
> {
  constructor(props: BillingTablePropsType) {
    super(props);
    this.state = {
      selected: null
    };
  }

  selectSlice = (value: string | null): * => {
    this.setState({ selected: value });
  };

  render(): React$Element<*> {
    const width = 360;
    const charts = this.props.charts && AppendHelpInfo(this.props.charts);

    if (charts) {
      const pie = d3
        .pie()
        .value(function(item: ChartType): number {
          return item.total;
        })
        .sort(function(a: ChartType, b: ChartType): number {
          return a.id.localeCompare(b.id);
        })(charts);

      return (
        <Fragment>
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            height={width}
            width={width}
            className="pie-chart"
          >
            <g transform={`translate(${width / 2},${width / 2})`}>
              {pie.map(
                (slice: *, index: number): React$Node => (
                  <Slice
                    item={slice}
                    color={ChartColors[index]}
                    selectSlice={this.selectSlice}
                    key={index}
                  />
                )
              )}
              <text
                textAnchor="middle"
                fill="black"
                fontSize="1rem"
                fontWeight="500"
                x="0"
                y="6"
              >
                $
                {this.state.selected
                  ? charts[
                    charts.findIndex(x => x.id === this.state.selected)
                  ].total.toFixed(2)
                  : this.props.total}
              </text>
            </g>
          </svg>
          <Table className="app-table center">
            <TableHead>
              <TableRow>
                <TableCell className="blue-shine" padding="dense">
                  Color
                </TableCell>
                <TableCell className="blue-shine" padding="dense">
                  Item
                </TableCell>
                <TableCell className="blue-shine" padding="dense">
                  Qty
                </TableCell>
                <TableCell className="blue-shine" padding="dense">
                  Unit Price(AUD)
                </TableCell>
                <TableCell className="blue-shine" padding="dense">
                  Total Fee(AUD)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {charts.map(
                (chart: ChartType, i: number): React$Node => (
                  <TableRow
                    key={i}
                    className={this.state.selected === chart.id ? "active" : ""}
                  >
                    <TableCell padding="dense">
                      <span
                        className="color-box"
                        style={{ backgroundColor: ChartColors[i] }}
                      />
                    </TableCell>
                    <TableCell padding="dense">{chart.name}</TableCell>
                    <TableCell padding="dense">
                      {chart.count}
                      {chart.help && (
                        <Tooltip title={chart.help}>
                          <HelpIcon
                            className="grey-icon left-margin-5"
                            style={{ verticalAlign: "bottom" }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell padding="dense">
                      {chart.displayPrice}
                    </TableCell>
                    <TableCell padding="dense">
                      ${parseFloat(chart.total).toFixed(2)}
                    </TableCell>
                  </TableRow>
                )
              )}
              <TableRow key="total">
                <TableCell padding="dense" />
                <TableCell padding="dense" className="border">
                  Total
                </TableCell>
                <TableCell padding="dense" />
                <TableCell padding="dense" />
                <TableCell padding="dense" className="border">
                  ${this.props.total}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Fragment>
      );
    } else {
      return <LoadingProgress />;
    }
  }
}

export default BillingTable;
